import CollapsibleContainer from "@via/components/collapsible-container/collapsible-container";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import { usePipelineLint } from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { LintFinding, LintSeverity } from "common";

const BANNER_TYPE: Record<LintSeverity, BannerType> = {
  [LintSeverity.Error]: BannerType.Error,
  [LintSeverity.Warning]: BannerType.Warning,
  [LintSeverity.Info]: BannerType.Info,
};

const GROUP_TITLE: Record<LintSeverity, string> = {
  [LintSeverity.Error]: "Will likely fail the run",
  [LintSeverity.Warning]: "Worth checking",
  [LintSeverity.Info]: "For information",
};

const formatFinding = (finding: LintFinding): string => {
  const where = finding.locations
    .map(l => (l.line ? `${l.file}:${l.line}` : l.file))
    .join(", ");
  const suffix = where ? ` (${where})` : "";
  return `${finding.title}. ${finding.detail} ${finding.fix}${suffix}`;
};

interface PipelineLintPanelProps {
  pipelineId?: number | string;
}

const PipelineLintPanel = ({ pipelineId }: PipelineLintPanelProps) => {
  const { data, isLoading, isError } = usePipelineLint(pipelineId);

  if (isLoading || isError || !data || data.findings.length === 0) {
    return null;
  }

  const order = [LintSeverity.Error, LintSeverity.Warning, LintSeverity.Info];

  return (
    <CollapsibleContainer
      title={`Pipeline analysis (${data.findings.length})`}
      defaultOpen>
      {order.map(severity => {
        const group = data.findings.filter(f => f.severity === severity);
        if (group.length === 0) {
          return null;
        }
        return (
          <MessageBanner
            key={severity}
            type={BANNER_TYPE[severity]}
            title={GROUP_TITLE[severity]}
            messages={group.map(formatFinding)}
          />
        );
      })}
    </CollapsibleContainer>
  );
};

export default PipelineLintPanel;
